import {inject, Injectable, signal, WritableSignal} from '@angular/core';
import {Apollo, gql} from 'apollo-angular';
import {
  CustomerProfile,
  CustomerUpsertV2Input,
  Mutation,
  Query
} from '../interfaces/generated/graphql';
import {captureException} from '@sentry/angular';
import {BasicModalService} from './basic-modal.service';
import {CustomerProfileFragment} from '../fragments/customer-profile.fragment';

export type AlgoliaCustomer = {
  name: string,
  phoneNumber: string,
  phoneNumberCountryCode: string,
  email: string,
  accountType: string,
  hasPassword: boolean,
  vismaCustomerId: string,
  objectID: string,
  address: string,
  zipCode: string,
  city: string,
  countryCode: string,
  cvr: string,
  ean: string,
  companyName: string,
  _highlightResult: {
    name: { value: string, matchLevel: string, matchedWords: string[] },
    phoneNumber: { value: string, matchLevel: string, matchedWords: string[] },
    email: { value: string, matchLevel: string, matchedWords: string[] }
    vismaCustomerId: { value: string, matchLevel: string, matchedWords: string[] }
  }
};
export type SelectedCustomer = { customer: CustomerProfile | null, loading: boolean };
export type NewsletterSubscriptions = {
  isSubscribedToSupervinNewsletter: boolean,
  isSubscribedToOneToTwoEmailsAWeek: boolean
};

@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  private apollo = inject(Apollo);
  private basicModalService = inject(BasicModalService);


  selectedCustomer = signal<SelectedCustomer>({customer: null, loading: false});
  selectedCustomerNewsletter: WritableSignal<{
    data: NewsletterSubscriptions | null,
    loading: boolean
  }> = signal({
    loading: false,
    data: null
  });

  selectCustomerByVismaId(vismaId: string) {
    this.setCustomerData(vismaId);
    this.setCustomerNewsletterData(vismaId);
  }

  selectCustomerByAlgoliaHit(hit: AlgoliaCustomer) {
    const customer = {
      id: hit.objectID,
      vismaId: hit.objectID,
      fullName: hit.name,
      emailForOrderConfirmation: hit.email,
      phoneNumber: hit.phoneNumber?.toString(),
      accountType: hit.accountType.toString(),
      hasPassword: hit.hasPassword,
      isEmailConfirmed: false,
      defaultBillingAddress: null,
      cvr: hit.cvr,
      ean: hit.ean,
      companyName: hit.companyName,
    } as CustomerProfile;

    if (hit.address) {
      customer.defaultBillingAddress = {
        id: hit.objectID,
          address: hit.address,
          addressZip: hit.zipCode,
          addressCity: hit.city,
          countryCode: hit.countryCode,
      }
    }

    this.selectedCustomer.set({
      customer, loading: false
    });
    this.setCustomerNewsletterData(hit.objectID);
  }

  clearCustomer() {
    this.selectedCustomer.set({customer: null, loading: false});
  }

  private setCustomerData(vismaId: string) {
    this.selectedCustomer.set({customer: null, loading: true});
    this.getCustomerByVismaId(vismaId).then((customer) => {
      if (customer) {
        this.selectedCustomer.set({customer, loading: false});
      }
    }).catch((reason) => {
      this.selectedCustomer.set({customer: null, loading: false});
      captureException(reason);
      this.basicModalService.showErrorModal('Der skete en fejl. Kunne ikke hente kundens oplysninger');
    });
  }

  private setCustomerNewsletterData(vismaId: string) {
    this.selectedCustomerNewsletter.set({loading: true, data: null});
    return this.getHubspotNewsletterByVismaId(vismaId).then((newsletter) => {
      this.selectedCustomerNewsletter.set({
        loading: false,
        data: {
          isSubscribedToSupervinNewsletter: newsletter?.isSubscribedToSupervinNewsletter ?? false,
          isSubscribedToOneToTwoEmailsAWeek: newsletter?.isSubscribedToOneToTwoEmailsAWeek ?? false
        }
      });
    }).catch((reason) => {
      this.selectedCustomerNewsletter.set({
        loading: false,
        data: null
      });
      captureException(reason);
    });
  }

  private getCustomerByVismaId(vismaId: string) {
    return this.apollo.query<Query>({
      query: gql`
        query getCustomerByVismaId($vismaId: ID!) {
          getCustomerByVismaId(vismaId: $vismaId) {
            ...CustomerProfileFragment
          }
        }
        ${CustomerProfileFragment}
      `,
      variables: {
        vismaId
      }
    }).toPromise().then((res) => res?.data?.getCustomerByVismaId);
  }

  getHubspotNewsletterByVismaId(vismaId: string) {
    return this.apollo.query<Query>({
      query: gql`
        query getHubspotContactByVismaId($vismaId: ID!) {
          getHubspotContactByVismaId(vismaId: $vismaId) {
            id
            isSubscribedToSupervinNewsletter
            isSubscribedToOneToTwoEmailsAWeek
          }
        }
      `,
      variables: {
        vismaId
      }
    }).toPromise().then((res) => res?.data?.getHubspotContactByVismaId);
  }

  getCityByZipCode(zipcode: string, countryCode: string = 'DK') {
    return this.apollo.query<Query>({
      query: gql`
        query getCityByZipCode($countryCode: String!, $zipcode: String!) {
          getCityByZipcode(countryCode: $countryCode, zipcode: $zipcode)
        }
      `,
      variables: {
        zipcode,
        countryCode
      }
    }).toPromise().then((res) => res?.data?.getCityByZipcode);
  }

  upsertCustomer(vismaId: string | null | undefined, customer: CustomerUpsertV2Input, isCreateFlow: boolean) {
    const phoneNumber = customer.phoneNumber;
    if (!customer.fullName) {
      customer.fullName = phoneNumber ?? '';
    }
    return this.upsertCustomerMutation(
      vismaId,
      isCreateFlow,
      isCreateFlow,
      customer
    );
  }

  sendFinishSignupSmsReminder(vismaId: string) {
    return this.apollo.mutate<Mutation>({
      mutation: gql`
        mutation sendFinishSignupSmsReminder($vismaId: ID!) {
          sendFinishSignupSmsReminder(vismaId: $vismaId) {
            type
            message
          }
        }
      `,
      variables: {
        vismaId
      }
    }).toPromise().then((res) => res?.data?.sendFinishSignupSmsReminder);
  }

  private upsertCustomerMutation(vismaId: string | null | undefined, forceCreateFlexposCustomer: boolean, sendFinishSignUpReminder: boolean, customer: CustomerUpsertV2Input | null) {
    return this.apollo.mutate<Mutation>({
      mutation: gql`
        mutation upsertCustomerV2($vismaId: ID, $customer: CustomerUpsertV2Input!, $forceCreateFlexposCustomer: Boolean, $sendFinishSignUpReminder: Boolean) {
          upsertCustomerV2(vismaId: $vismaId, data: $customer, forceCreateFlexposCustomer: $forceCreateFlexposCustomer, sendFinishSignUpReminder: $sendFinishSignUpReminder) {
            ...CustomerProfileFragment
          }
        }
        ${CustomerProfileFragment}
      `,
      variables: {
        vismaId,
        customer,
        forceCreateFlexposCustomer,
        sendFinishSignUpReminder
      },
    }).toPromise().then((res) => res?.data?.upsertCustomerV2);
  }
}
